import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/react-hooks'
import classNames from 'classnames'
import { useHistory, useLocation } from 'react-router-dom'

import Button from '../../../button'
import { LoadingModal } from '../../../loading/modal'
import { getModalLocation } from '../../../../lib/modal'
import { MODALS } from '../../../../constants'
import {
  findDefaultSVODCreditCard,
  findDefaultTVODCreditCard
} from '../../../../lib/account'
import { useSetCreditcardDefaultMutation } from '../../../../hooks/use-set-creditcard-default-mutation'
import SUBSCRIPTION_CONFIRMATION_VIEW_QUERY from '../../../../../graphql/queries/subscription-confirmation-view.gql'
import ACCOUNT_QUERY_WITH_DIR from '../../../../../graphql/queries/account-with-directives.gql'
import { TESTERS } from '../../../../lib/tester-classnames'
import styles from './subscription-confirmation.css'

/**
 * Displays the completed payment-details update and prepares the saved card for future payments.
 *
 * Key features:
 * - Fetches the confirmation content for the completed order.
 * - Sets the first available card as the SVOD and, when needed, TVOD default.
 * - Renders the order details and continues to profile selection from the CTA.
 */
function SubscriptionConfirmationOrder({ payload }) {
  const history = useHistory()
  const location = useLocation()
  const hasSetDefaultCards = useRef(false)
  const setCreditcardDefault = useSetCreditcardDefaultMutation()
  const { orderId } = payload

  const {
    data: confirmationData,
    loading: confirmationLoading,
    error: confirmationError
  } = useQuery(SUBSCRIPTION_CONFIRMATION_VIEW_QUERY, {
    variables: { orderId },
    fetchPolicy: 'network-only'
  })

  const {
    data: accountData,
    loading: accountLoading
  } = useQuery(ACCOUNT_QUERY_WITH_DIR, {
    variables: {
      withCreditcards: true,
      withSubscription: true
    },
    fetchPolicy: 'network-only'
  })

  const account = accountData?.account || {}
  const hasDefaultSVODCard = findDefaultSVODCreditCard(account)
  const hasDefaultTVODCard = findDefaultTVODCreditCard(account)
  const paymentMethodId = account.creditcards?.[0]?.id

  useEffect(() => {
    if (
      accountLoading ||
      confirmationLoading ||
      confirmationError ||
      hasSetDefaultCards.current ||
      hasDefaultSVODCard ||
      !paymentMethodId
    ) {
      return
    }
    // Preserve the Apollo cache updates rerender the component while mutations are in flight.
    hasSetDefaultCards.current = true

    const updateDefaultCard = (productType) => {
      setCreditcardDefault(paymentMethodId, productType)
        .then(() => {
          if (process.env.NODE_ENV !== 'production') {
            console.info(`Default ${productType} payment method set`)
          }
        })
        .catch(() => {
          if (process.env.NODE_ENV !== 'production') {
            console.info(`Error occurred setting default ${productType} payment method`)
          }
        })
    }

    updateDefaultCard('SVOD')

    if (!hasDefaultTVODCard) {
      updateDefaultCard('TVOD')
    }
  }, [
    accountLoading,
    confirmationError,
    confirmationLoading,
    hasDefaultSVODCard,
    hasDefaultTVODCard,
    paymentMethodId,
    setCreditcardDefault
  ])

  if (confirmationLoading || confirmationError || accountLoading) {
    return <LoadingModal />
  }

  const subscriptionConfirmationView = confirmationData?.subscriptionConfirmationView || {}
  const {
    header = '',
    details = [],
    cta = ''
  } = subscriptionConfirmationView

  // Preserve the subscription-confirmation journey after payment recovery.
  const onCtaClick = () => {
    history.push(
      getModalLocation(
        location,
        MODALS.qsParams.profilesSwitch
      )
    )
  }

  return (
    <div className={classNames(styles.wrapper, TESTERS.SIGNUP.CONFIRMATION_ORDER)}>
      <div className={styles.header}>{header}</div>
      <div className={styles.confirmationBox}>
        <div className={styles.details}>
          {
            // A detail label can be backend-provided text or an image, such as a plan logo.
            details.map(detail => (
              <div key={detail.name} className={styles.detail}>
                <div className={styles.detailLabel}>
                  {
                    detail.image
                      ? <img className={styles.detailImage} alt={detail.name} src={detail.image} />
                      : detail.name
                  }
                </div>
                <div>
                  {detail.value}
                </div>
              </div>
            ))
          }
        </div>
      </div>
      <Button
        className={styles.button}
        text={cta}
        e2eIdentifier={cta}
        onClick={onCtaClick}
      />
    </div>
  )
}

SubscriptionConfirmationOrder.propTypes = {
  payload: PropTypes.shape({
    orderId: PropTypes.string.isRequired
  }).isRequired
}

export default SubscriptionConfirmationOrder
